import { DollarSign } from 'lucide-react'

import type { DesiredLocation, ExactLocation } from 'src/features/consumers/schemas/consumer'
import DateInput from 'src/shared/components/ui/inputs/date-input'
import FloatingLabelInput from 'src/shared/components/ui/inputs/floating-label-input-animated'
import RateInput from 'src/shared/components/ui/inputs/rate-input'
import IconButtonSelect from 'src/shared/features/marketplace-instant-match/components/ui/icon-button-select'
import { cn } from 'src/shared/utils/cn'
import { formatInternationalPhoneNumber } from 'src/shared/utils/format/format-intl-phone-number'
import { formatCurrency, parseCurrencyInput } from 'src/shared/utils/format-currency'

import type { BusinessInfo } from '../schemas/business-create'
import {
  BusinessEnumMap,
  BusinessInfoFieldsEditType,
  BusinessInfoLabels,
  FieldTypeEnum,
} from '../schemas/business-create'

import BusinessDesiredLocationSelector from './bc-desired-location-selector'
import BusinessExactLocationSelector from './bc-exact-location-selector'
import LocationPill from './ui/bc-location-pill'

type BusinessInfoInputFieldProps = {
  infoField: keyof BusinessInfo
  value: any
  onChange: (value: any) => void
}

const BusinessInfoInputField = ({ infoField, value, onChange }: BusinessInfoInputFieldProps) => {
  const infoEditType = BusinessInfoFieldsEditType[infoField]

  switch (infoEditType) {
    case FieldTypeEnum.TEXT:
      return (
        <FloatingLabelInput
          label={BusinessInfoLabels[infoField]}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )

    case FieldTypeEnum.PHONE:
      return (
        <FloatingLabelInput
          label={BusinessInfoLabels[infoField]}
          value={formatInternationalPhoneNumber(value?.toString() || '')}
          onChange={(event) => {
            const rawValue = event.target.value.replace(/[^\d+]/g, '')
            onChange(rawValue)
          }}
          autoComplete="tel-national"
        />
      )

    case FieldTypeEnum.NUMBER:
      return (
        <FloatingLabelInput
          label={BusinessInfoLabels[infoField]}
          value={value?.toString()}
          onChange={(event) => {
            const numericText = event.target.value.replace(/[^0-9.]/g, '')
            onChange(numericText)
          }}
        />
      )

    case FieldTypeEnum.CURRENCY:
      return (
        <div className="flex flex-row items-center gap-2">
          <DollarSign className="size-5 text-muted" />
          <FloatingLabelInput
            label={BusinessInfoLabels[infoField]}
            value={formatCurrency(value)}
            onChange={(event) => {
              const numericText = event.target.value.replace(/[^0-9.]/g, '')
              onChange(parseCurrencyInput(numericText))
            }}
          />
        </div>
      )

    case FieldTypeEnum.PRICE_RANGE:
      return (
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-row items-center gap-2">
            <DollarSign className="size-5 text-muted" />
            <FloatingLabelInput
              label="Min"
              value={formatCurrency(value?.min)}
              onChange={(event) => {
                const numericText = event.target.value.replace(/[^0-9.]/g, '')
                onChange({ ...value, min: parseCurrencyInput(numericText) })
              }}
            />
          </div>
          <div>-</div>
          <div className="flex flex-row items-center gap-2">
            <DollarSign className="size-5 text-muted" />
            <FloatingLabelInput
              label="Max"
              value={formatCurrency(value?.max)}
              onChange={(event) => {
                const numericText = event.target.value.replace(/[^0-9.]/g, '')
                onChange({ ...value, max: parseCurrencyInput(numericText) })
              }}
            />
          </div>
        </div>
      )

    case FieldTypeEnum.RANGE:
      return (
        <div className="flex flex-row items-center gap-2">
          <FloatingLabelInput
            label="Min"
            value={formatCurrency(value?.min)}
            onChange={(event) => {
              const numericText = event.target.value.replace(/[^0-9.]/g, '')
              onChange({ ...value, min: parseCurrencyInput(numericText) })
            }}
          />
          <div>-</div>
          <FloatingLabelInput
            label="Max"
            value={formatCurrency(value?.max)}
            onChange={(event) => {
              const numericText = event.target.value.replace(/[^0-9.]/g, '')
              onChange({ ...value, max: parseCurrencyInput(numericText) })
            }}
          />
        </div>
      )

    case FieldTypeEnum.RATE:
      return <RateInput rate={value || 0} setRate={(rate) => onChange(rate)} defaultValue={6} step={0.25} />

    case FieldTypeEnum.BOOLEAN: {
      const options = {
        true: { label: 'Yes' },
        false: { label: 'No' },
      }

      return (
        <div className="grid w-full grid-cols-2 gap-2.5">
          {Object.entries(options).map(([currentValueText, { label }]) => {
            const currentValue = currentValueText === 'true'
            return (
              <IconButtonSelect
                key={currentValueText}
                label={label}
                onClick={() => onChange(currentValue)}
                textClassName="text-base font-semibold"
                className={cn(
                  'h-[65px] min-h-0 w-full min-w-0',
                  Boolean(value) === Boolean(currentValue) && 'bg-primary text-white',
                )}
              />
            )
          })}
        </div>
      )
    }

    case FieldTypeEnum.SELECT: {
      const selectOptionMap = BusinessEnumMap[infoField as keyof typeof BusinessEnumMap]
      if (!selectOptionMap) return null

      const selectOptions = Object.entries(selectOptionMap).map(([key, { label }]) => ({
        value: key,
        label,
      }))

      return (
        <div className="grid w-full grid-cols-2 gap-2.5">
          {selectOptions.map((option) => {
            const isSelected = value === option.value
            return (
              <IconButtonSelect
                key={option.value}
                label={option.label}
                onClick={() => onChange(option.value)}
                textClassName="text-base font-semibold"
                className={cn('h-[65px] min-h-0 w-full min-w-0', isSelected && 'bg-primary text-white')}
              />
            )
          })}
        </div>
      )
    }

    case FieldTypeEnum.DATE:
      return <DateInput label={BusinessInfoLabels[infoField]} value={value} onChangeText={(date) => onChange(date)} />

    case FieldTypeEnum.EXACT_LOCATIONS:
      return (
        <div className="flex w-full flex-col gap-4">
          <BusinessExactLocationSelector
            onPlaceSelected={(place) => {
              if (Array.isArray(value)) {
                onChange([
                  ...value,
                  {
                    latitude: place.latitude,
                    longitude: place.longitude,
                    propertyStreetAddress1: place.street,
                    propertyCity: place.city,
                    propertyState: place.state,
                    propertyZip: place.zip,
                    placeId: place.placeId,
                  },
                ] as ExactLocation[])
                return
              }

              onChange([
                {
                  latitude: place.latitude,
                  longitude: place.longitude,
                  propertyStreetAddress1: place.street,
                  propertyCity: place.city,
                  propertyState: place.state,
                  propertyZip: place.zip,
                  placeId: place.placeId,
                },
              ])
            }}
            selectedLocations={value || []}
          />
          <div className="flex max-w-[500px] shrink-0 grow-0 flex-row flex-wrap gap-2">
            {(value || []).map((location: ExactLocation) => (
              <LocationPill
                key={location.propertyStreetAddress1}
                location={`${location.propertyStreetAddress1}, ${location.propertyState}`}
                onRemoveLocation={() => {
                  if (Array.isArray(value)) {
                    onChange(
                      value.filter(
                        (item: ExactLocation) => item.propertyStreetAddress1 !== location.propertyStreetAddress1,
                      ),
                    )
                    return
                  }
                  onChange([])
                }}
              />
            ))}
          </div>
        </div>
      )

    case FieldTypeEnum.LOCATIONS:
      return (
        <div className="flex w-full flex-col gap-4">
          <BusinessDesiredLocationSelector
            onPlaceSelected={(place) => {
              if (Array.isArray(value)) {
                onChange([
                  ...value,
                  {
                    latitude: place.latitude,
                    longitude: place.longitude,
                    name: `${place.city}, ${place.state}`,
                    placeId: place.placeId,
                  },
                ] as DesiredLocation[])
                return
              }

              onChange([
                {
                  latitude: place.latitude,
                  longitude: place.longitude,
                  name: `${place.city}, ${place.state}`,
                  placeId: place.placeId,
                },
              ])
            }}
            selectedLocations={value || []}
          />
          <div className="flex max-w-[500px] shrink-0 grow-0 flex-row flex-wrap gap-2">
            {(value || []).map((location: DesiredLocation) => (
              <LocationPill
                key={location.name}
                location={location.name}
                onRemoveLocation={() => {
                  if (Array.isArray(value)) {
                    onChange(value.filter((item: DesiredLocation) => item.name !== location.name))
                    return
                  }
                  onChange([])
                }}
              />
            ))}
          </div>
        </div>
      )

    default:
      return (
        <FloatingLabelInput
          label={BusinessInfoLabels[infoField]}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )
  }
}

export default BusinessInfoInputField
